import { useDocumentTitle } from "usehooks-ts";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { executeQuery } from "../../utils";
import { useEffect, useState } from "react";
import { DmLoadingSpinner } from "../utils/functions";
import {
  DataTableContextProvider,
  useDataTable,
} from "./DataTable/DataTableContext";
import { DataTable } from "./DataTable/DataTable";
import { TableHeaderFilterField } from "./DataTable/TableHeaderFilterField";

/**
 * Describes the price scheme selector / response values for price-scheme/published
 */
type PriceSchemeSelector = {
  key: string;
  default?: boolean;
};

/**
 * A license option for the district, as defined in the backend
 */
type LicenseOption = {
  price: {
    plus: number;
    integral: number;
  };
  discount: number;
  priceWithDiscount: {
    plus: number;
    integral: number;
  };
  studentCount: number;
};

/**
 * The available district license types.
 */
type DistrictLicenseType = "6-12" | "7-12" | "9-12" | "6-8" | "7-8";

/**
 * Describes a district, as defined in the backend
 */
type District = {
  name: string;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  phone: string;
  url: string;
  licenseOptions?: {
    [key in DistrictLicenseType]?: LicenseOption;
  };
};

const PRORATED_LABELS = {
  originalPlusPrice: "Prorated Plus Price",
  originalIntegralPrice: "Prorated Integral Price",
  discount: "Discount",
  finalPlusPrice: "Final Prorated Plus Price",
  finalIntegralPrice: "Final Prorated Integral Price",
};

const ORIGINAL_LABELS = {
  originalPlusPrice: "Original Plus Price",
  originalIntegralPrice: "Original Integral Price",
  discount: "Discount",
  finalPlusPrice: "Final Plus Price",
  finalIntegralPrice: "Final Integral Price",
};

export default function QuoteDistrict() {
  useDocumentTitle("Quote Info");
  const params = useParams();
  const schoolId = params.schoolId;

  const [priceScheme, setPriceScheme] = useState<string>("");
  const { refetchTableData } = useDataTable();

  // Fetch price scheme keys
  const {
    data: priceSchemes,
    isSuccess: priceSchemesSuccess,
    isLoading: priceSchemesLoading,
  } = useQuery<Array<PriceSchemeSelector>>({
    queryKey: ["price_schemes"],
    queryFn: () =>
      executeQuery({
        path: "/public/quote/price-scheme/published",
        customerServiceHeader: true,
      }),
    enabled: true,
  });

  useEffect(() => {
    if (priceSchemesSuccess && priceSchemes?.length) {
      // set the default price scheme
      setPriceScheme(
        priceSchemes.find((scheme) => scheme.default)?.key ||
          priceSchemes[0].key
      );
    }
  }, [priceSchemesSuccess, priceSchemes]);

  if (priceSchemesLoading || !priceSchemes) {
    return <DmLoadingSpinner message="Loading Price Schemes..." />;
  }

  if (!priceSchemes.length) {
    return (
      <p className="py-10 text-center text-red-500">No price schemes found</p>
    );
  }

  return (
    <main className="flex flex-col gap-6 p-6">
      <header className="my-4 flex flex-row items-center gap-4">
        <h1 className="text-2xl font-semibold">Quote Info</h1>
        <select
          id="year"
          name="year"
          value={priceScheme}
          onChange={(e) => {
            setPriceScheme(e.target.value);
            refetchTableData();
          }}
          className="block w-min rounded-md border-gray-300 text-sm"
        >
          <option disabled value="-">
            Select Price Scheme
          </option>
          {priceSchemes.map((scheme) => (
            <option key={scheme.key} value={scheme.key}>
              {scheme.key}
            </option>
          ))}
        </select>
      </header>
      {priceScheme && (
        <DataTableContextProvider
          source={`/manager_new/manage/school-or-district-by-id/${params.type}/${params.districtId}/${priceScheme}`}
          defaults={{ sortBy: [{ id: "id" }], filterBy: [] }}
          fetchOnLoad
          downloadButton={false}
          sortable={[
            "name",
            "level",
            "students",
            "gradespk_5",
            "grades6_8",
            "grades9_12",
            "price_plus",
            "price_integral",
            "discount",
            "final_plus",
            "final_integral",
            "id",
          ]}
          sortSource="client"
          sortTypes={{
            id: (rowA, rowB, columnId, desc) => {
              const a = rowA.values[columnId] as string | undefined;
              const b = rowB.values[columnId] as string | undefined;
              // for initial value: ensure that the school the user clicked on is at the top
              if (desc === undefined) {
                return a === schoolId ? -1 : 1;
              }

              if (a === undefined && b === undefined) {
                return 0;
              }
              if (a === undefined) {
                return -1;
              }
              if (b === undefined) {
                return 1;
              }
              return a.localeCompare(b);
            },
          }}
          filterable={["name", "level", "id"]}
          filterOverrides={{
            level: ({ column }) => (
              <TableHeaderFilterField column={column} className="w-20" />
            ),
          }}
          filterSource="client"
          globalFilter
          alternateKeyForPersistentState="school-or-district-by-id"
        >
          {params.type === "district" && (
            <DistrictQuoteTable priceScheme={priceScheme} />
          )}
          <DataTable />
        </DataTableContextProvider>
      )}
    </main>
  );
}

const PricingCell: React.FC<{
  label: string;
  priceScheme: string;
  pricing: LicenseOption;
}> = ({ label, pricing, priceScheme }) => {
  const isProrated = priceScheme.toLowerCase().includes("prorated");

  const labels = isProrated ? PRORATED_LABELS : ORIGINAL_LABELS;

  return (
    <td className="px-4 py-8 align-top">
      <b>{label}</b>
      <div>{`(${formatNumber(pricing.studentCount)} students)`}</div>
      <div>{`${labels.originalPlusPrice}: ${formatNumber(
        pricing.price.plus
      )}`}</div>
      <div>{`${labels.originalIntegralPrice}: ${formatNumber(
        pricing.price.integral
      )}`}</div>
      <div>{`${labels.discount}: ${pricing.discount}%`}</div>
      <div>{`${labels.finalPlusPrice}: ${formatNumber(
        pricing.priceWithDiscount.plus
      )}`}</div>
      <div>{`${labels.finalIntegralPrice}: ${formatNumber(
        pricing.priceWithDiscount.integral
      )}`}</div>
    </td>
  );
};

const DistrictQuoteTable = ({ priceScheme }: { priceScheme: string }) => {
  const { district } = useDataTable<{ district: District }>();

  if (!district) {
    return <DmLoadingSpinner message="Loading District Data..." />;
  }

  return (
    <table className="mb-4 w-full border-collapse rounded border border-gray-700 shadow-sm">
      <tbody>
        <tr>
          <td className="min-w-[220px] border !border-r !border-solid border-none border-gray-700 px-4 py-8 align-middle">
            <div className="text-lg font-semibold">{district.name}</div>
            <div className="mb-1 text-sm text-gray-700">
              {district.address.street},<br />
              {district.address.city}, {district.address.state}{" "}
              {district.address.zip}
            </div>
            <div className="mb-1 text-sm text-gray-700">{district.phone}</div>
            <div className="text-sm">
              <a
                href={district.url}
                target="_blank"
                rel="noreferrer"
                className="text-blue-500 hover:underline"
              >
                SchoolDigger Link
              </a>
            </div>
          </td>
          {district.licenseOptions &&
            Object.entries(district.licenseOptions).map(([key, value]) => (
              <PricingCell
                key={key}
                label={"District " + key}
                pricing={value}
                priceScheme={priceScheme}
              />
            ))}
        </tr>
      </tbody>
    </table>
  );
};

/**
 * Formats a number to a string with commas and two decimal places
 * @param num The number to format
 * @returns The formatted number as a string
 */
function formatNumber(num: number) {
  if (isNaN(num)) {
    return "-";
  }
  return num === Math.round(num)
    ? num.toLocaleString()
    : Intl.NumberFormat("en-us", {
        style: "decimal",
        minimumFractionDigits: 2,
      }).format(num);
}
